<template>
  <div class="payment-method-screen">
    <CheckoutLoading v-if="!selectedPlan" />
    <form v-else id="checkout-form" autocomplete="off" @submit.prevent novalidate>
      <ScreenHeading>
        {{ heading }}
      </ScreenHeading>
      <PaymentDetails v-if="showExistingPaymentDetails" />
      <div v-else>
        <RecurlyBillingInputGroup
          v-model:form="form"
          @selectPaymentMethod="updatePaymentMethod"
          :isUpdateAttempt="isSubmitAttempt"
          :paymentRequired="paymentRequired"
        >
          <RecurlyCreditCardFields v-if="paymentRequired && paymentMethod === PaymentMethodType.creditCard" ref="creditCardFields" />
        </RecurlyBillingInputGroup>
        <CashAppContainer v-if="paymentRequired && paymentMethod === PaymentMethodType.cashApp" container-id="cashapp-container" />
        <RecurlyTokenErrors />
      </div>
      <Recurly3DSContainer />
      <PlanSummaryCardContainer />
      <p class="fineprint">
        By clicking {{ buttonText }} you agree to Output’s <a :href="termsUrl" target="_blank">Terms of Use</a>,
        <a :href="privacyPolicyUrl" target="_blank">Privacy Policy</a>{{ fineprintDisclaimer }}
      </p>
      <ScreenNextSectionBtn :disabled="!readyToSubmit" :text="buttonText" hide-enter @navigateClick="submit" data-test="checkout-cta" />
      <Compliance class="icons" />
    </form>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'
import { PaymentMethodType } from '@/types'
import { useAmplitude, BrazeEvents, useGtm } from '@outputinc/o-analytics'
import PaymentDetails from '@/components/shared/payment-details/PaymentDetails.vue'
import PlanSummaryCardContainer from '@/components/shared/plan-summary/PlanSummaryCardContainer.vue'
import ScreenHeading from '@/components/shared/screens/ScreenHeading.vue'
import ScreenNextSectionBtn from '@/components/shared/screens/ScreenNextSectionBtn.vue'
import RecurlyBillingInputGroup from '@/components/shared/recurly/RecurlyBillingInputGroup.vue'
import RecurlyCreditCardFields from '@/components/shared/recurly/RecurlyCreditCardFields.vue'
import Recurly3DSContainer from '@/components/shared/recurly/Recurly3DSContainer.vue'
import RecurlyTokenErrors from '@/components/shared/recurly/RecurlyTokenErrors.vue'
import CashAppContainer from '@/components/shared/recurly/CashAppContainer.vue'
import CheckoutLoading from '@/components/shared/CheckoutLoading.vue'
import Compliance from '@/components/shared/svg/Compliance.vue'
import { useBrazeStore } from '@/store/BrazeStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useCouponStore } from '@/store/CouponStore'
import { useUserStore } from '@/store/UserStore'
import { useScreenStore } from '@/store/ScreenStore'
import { usePlanStore } from '@/store/PlanStore'
import { useRecurlyStore } from '@/store/RecurlyStore'
import { useProductStore } from '@/store/ProductStore'
import { useTrialStatus } from '@/composables/useTrialStatus'
import { useModalStore } from '@/store/ModalStore'
import { useErrorsStore } from '@/store/ErrorsStore'
import { usePriceStore } from '@/store/PriceStore'

const couponStore = useCouponStore()
const userStore = useUserStore()
const recurlyStore = useRecurlyStore()
const checkoutStore = useCheckoutStore()
const planStore = usePlanStore()
const productStore = useProductStore()
const errorsStore = useErrorsStore()
const priceStore = usePriceStore()
const { setScreenComplete, setBusy, setCanNavigateNext } = useScreenStore()
const { planHasTrial } = useTrialStatus()
const { trackEmailUser } = useBrazeStore()
const { trigger: triggerModal } = useModalStore()

const { email, outputUid, country: userCountry, isAnyOutputResubscriber, isDirectResubscriber, userType } = storeToRefs(userStore)

const {
  isPayOncePlan,
  paymentRequired,
  selectedPlan,
  selectedPlanCode,
  selectedPlanInterval,
  selectedPlanIntervalAsNoun,
  selectedPlanLengthDisplay,
  selectedPlanProduct,
  selectedPlanTrialLengthDisplay
} = storeToRefs(planStore)

const { name: productName } = storeToRefs(productStore)

const { country, currency, firstName, hasValidPaymentDetails, lastName, postalCode, requiresPaymentDetails, noPaymentPlanType } = storeToRefs(checkoutStore)

const {
  appliedCouponTrial,
  appliedCouponTrialLengthPlural,
  hasAppliedCoupon,
  appliedCouponTrialLength,
  isFreeTrialCoupon,
  isTemporalCoupon,
  isSingleUseDiscountCoupon,
  couponDuration
} = storeToRefs(couponStore)
const { removeCoupon } = couponStore

const { token: recurlyToken, paymentMethod } = storeToRefs(recurlyStore)
const { selectedPlanDisplayPrice, selectedPlanDisplayUnitPrice, selectedPlanCouponAppliedPrice } = storeToRefs(priceStore)

const { setCheckoutProperty, clear3DSToken } = checkoutStore

const { handleTransactionError, refreshCashApp, onCreateNoPaySubscription, onCreateSubscription, process3DSecure, setPaymentMethod, submitSubscriptionForm } =
  recurlyStore

const { push: pushError, clear: clearErrors, setAppError } = errorsStore

const { track, Events } = useAmplitude()
const { track: gtmTrackEvent, Events: GtmEvents } = useGtm()
const isSubmitting = ref(false)

const creditCardFields = ref<InstanceType<typeof RecurlyCreditCardFields>>()

const isSubmitAttempt = ref(false)

const showExistingPaymentDetails = ref(hasValidPaymentDetails.value)
const readyToSubmit = computed(() => formIsValid.value || hasValidPaymentDetails.value)

const form = ref({
  firstName: '',
  lastName: '',
  ...(paymentRequired.value && { zipCode: '', country: userCountry.value || 'US' })
})

const inputsAreValid = computed(() => Object.values(form.value).every((value: any) => value.trim() !== ''))

const formIsValid = computed(() => {
  if (!paymentRequired.value) {
    return inputsAreValid.value
  }

  if (paymentMethod.value === PaymentMethodType.creditCard) {
    return inputsAreValid.value && creditCardFields.value?.isValid
  }

  return inputsAreValid.value
})

const trialLength = computed(() => (isFreeTrialCoupon.value ? appliedCouponTrialLength.value : selectedPlanTrialLengthDisplay.value))

const payOnceHeading = `Get lifetime access to ${productName.value} today`
const noTrialHeading = 'Start your plan today.'
const hasTrialHeading = computed(() => `Free ${trialLength.value} trial, no charges today.`)
const paymentRequiredHeading = computed(() => (planHasTrial.value ? hasTrialHeading.value : noTrialHeading))
const nfrHeading = computed(() => `Get ${noPaymentOfferLength.value} of ${productName.value} free`)
const noPaymentDefaultHeading = computed(() => `Redeem your ${noPaymentOfferLength.value} of ${productName.value}`)
const noPaymentHeading = computed(() => (noPaymentPlanType.value === 'nfr' ? nfrHeading.value : noPaymentDefaultHeading.value))

const heading = computed(() => {
  if (isPayOncePlan.value) {
    return payOnceHeading
  }

  if (paymentRequired.value) {
    return paymentRequiredHeading
  }

  return noPaymentHeading.value
})

const noPaymentOfferLength = computed(() => (appliedCouponTrial.value ? appliedCouponTrialLengthPlural.value : selectedPlanLengthDisplay.value))

const buttonText = computed(() => (planHasTrial.value ? 'Try it free' : 'Start now'))

const preTaxDetails = computed(() => (country.value === 'US' ? ' pre-tax' : ''))

const couponDurationDisplay = computed(() => {
  if (isTemporalCoupon.value) return couponDuration.value
  if (isSingleUseDiscountCoupon.value) return selectedPlanLengthDisplay.value
  if (appliedCouponTrial.value) return appliedCouponTrialLengthPlural.value
  return ''
})

const subscriptionPaymentDisclaimer = computed(() => {
  if (!paymentRequired.value) return ''

  const interval = selectedPlanIntervalAsNoun.value
  const preTax = preTaxDetails.value
  const suffix = ` until you cancel. You may cancel anytime by going to your Output Account and clicking "Cancel Subscription."`

  if (hasAppliedCoupon.value && couponDurationDisplay.value) {
    const duration = couponDurationDisplay.value
    return `, and that you will automatically be charged the membership fee (${selectedPlanDisplayPrice.value}/${interval}${preTax} for ${duration}, ${selectedPlanDisplayUnitPrice.value}/${interval}${preTax} after ${duration})${suffix}`
  }

  return `, and that you will automatically be charged the membership fee (${selectedPlanDisplayPrice.value}/${interval})${suffix}`
})
const payOncePaymentDisclaimer = computed(() => `, and that you will be charged ${selectedPlanDisplayPrice.value} + applicable tax.`)
const fineprintDisclaimer = computed(() => (isPayOncePlan.value ? payOncePaymentDisclaimer.value : subscriptionPaymentDisclaimer.value))

const directResubscriberProduct = computed(() => (isDirectResubscriber.value ? selectedPlanProduct.value : 'none'))

const emit = defineEmits(['navigateNext'])

onMounted(() => {
  track(Events.CHECKOUT_SUBSCRIPTION_PAYMENT, { products: [selectedPlanProduct.value] })
})

watchEffect(() => {
  if (formIsValid.value) {
    clearErrors()
  }
})

watchEffect(() => {
  setCanNavigateNext(readyToSubmit.value)
})

// Coupon changes alter the applied price, which the Cash App APM captured at init time. Refresh the widget so the new amount is reflected.
watch(selectedPlanCouponAppliedPrice, () => {
  if (paymentMethod.value === PaymentMethodType.cashApp) {
    refreshCashApp('#cashapp-container')
  }
})

const submit = async () => {
  if (isSubmitting.value) {
    return // prevent double submission
  }

  clearErrors()

  isSubmitting.value = true
  isSubmitAttempt.value = true

  try {
    if (requiresPaymentDetails.value) {
      const { firstName, lastName, zipCode, country } = form.value

      const checkoutPayload = {
        firstName,
        lastName,
        billingAddress: {
          postalCode: zipCode,
          country
        }
      }

      setCheckoutProperty(checkoutPayload)
    }

    const submitted = await submitSubscriptionForm()
    if (!submitted) {
      isSubmitting.value = false
      setBusy(false)
    }
  } catch (error) {
    handleError(error)
  }
}

const handleError = async (error: any) => {
  isSubmitting.value = false
  setBusy(false)

  if (error.type === 'validation') {
    if (error.message.includes('Subscription has reached max redemptions for this account')) {
      setAppError({
        title: 'Coupon code is not applicable',
        message: 'Your coupon code has already been redeemed or is not applicable to this plan.'
      })
      removeCoupon()
    } else {
      pushError(error)
    }
  } else if (error.message.includes('reCAPTCHA')) {
    pushError(error)
  } else if (error.type === 'transaction' && error.transactionError?.code === 'three_d_secure_action_required') {
    const threeDSecure = process3DSecure(error.transactionError.threeDSecureActionTokenId, submitSubscriptionForm)

    threeDSecure.attach(document.querySelector('#recurly-3ds-container'))

    pushError(error)
  } else {
    handleTransactionError()
    pushError(error)
    triggerModal({ error })
  }
}

onCreateSubscription((payload: any) => handleCreateResponse(payload))
onCreateNoPaySubscription((payload: any) => handleCreateResponse(payload))

const handleCreateResponse = (payload: any) => {
  // Recaptcha errors
  if (payload.errors?.length > 0) {
    const [firstError] = payload.errors
    setBusy(false)
    handleError(firstError)
    return
  }

  const {
    data: {
      createSubscription: { error, ...response }
    }
  } = payload

  if (error) {
    setBusy(false)
    handleError(error)
    clear3DSToken()
  } else {
    const { uuid, plan, trialStartedAt } = response
    const isTrial = !!trialStartedAt

    showExistingPaymentDetails.value = true

    gtmTrackEvent(GtmEvents.CHECKOUT_SUBSCRIPTION_SUCCESS_LEGACY, {
      subscription_id: uuid,
      directResubscriber: directResubscriberProduct.value,
      anyOutputResubscriber: isAnyOutputResubscriber.value,
      isTrial,
      outputUid: outputUid.value
    })

    trackEmailUser({
      externalId: outputUid.value,
      name: BrazeEvents.CHECKOUT_SUBSCRIPTION_COMPLETE,
      time: new Date().toISOString(),
      properties: {
        billing_interval: selectedPlanInterval.value,
        plan_code: plan.code,
        plan_price: selectedPlanDisplayPrice.value,
        products: selectedPlanProduct.value
      }
    })

    track(Events.CHECKOUT_SUBSCRIPTION_SUCCESS, {
      anyOutputResubscriber: isAnyOutputResubscriber.value,
      directResubscriber: directResubscriberProduct.value,
      billingCountry: country.value,
      currency: currency.value,
      detectedCountry: userCountry.value,
      email: email.value,
      firstName: firstName.value,
      id: outputUid.value,
      lastName: lastName.value,
      paymentMethod: paymentMethod.value,
      planCode: plan.code,
      postalCode: postalCode.value,
      products: [selectedPlanProduct.value],
      recurlyToken: recurlyToken.value,
      userType: userType.value,
      uuid,
      isTrial
    })

    isSubmitting.value = false

    setScreenComplete(true)

    setCheckoutProperty({ isComplete: true })

    emit('navigateNext')
  }
}

const termsUrl = computed(() => {
  return `${import.meta.env.VITE_OUTPUT_DOT_COM_HOSTNAME}/legal/terms`
})

const privacyPolicyUrl = computed(() => {
  return `${import.meta.env.VITE_OUTPUT_DOT_COM_HOSTNAME}/legal/privacy`
})

const updatePaymentMethod = (value: string) => {
  clearErrors()
  setPaymentMethod(value)
  track(Events.CHECKOUT_SUBSCRIPTION_PAYMENT_METHOD_SELECT, { paymentMethod: value, planCode: selectedPlanCode.value, products: selectedPlanProduct.value })
  gtmTrackEvent(GtmEvents.CHECKOUT_SUBSCRIPTION_PAYMENT_METHOD_SELECT, { paymentMethod: value, products: [selectedPlanProduct.value] })
}
</script>

<style lang="scss" scoped>
.payment-method-screen {
  max-width: $grid-breakpoint-sm;
  padding: 0 $spacer-3;
  margin: $spacer-5 0;

  @media (min-height: 769px) {
    margin: $spacer-9 0 $spacer-5;
  }

  @media screen and (min-width: $grid-breakpoint-sm) {
    padding: 0 $spacer-7;
  }
}

#checkout-form {
  margin-bottom: $spacer-6;
}

.fineprint {
  font-size: 10px;
  font-family: $font-family-base;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: var(--color-text-unselected);

  a {
    color: var(--color-text-primary);
    text-decoration: underline;
  }
}

.icons {
  margin-top: $spacer-6;
}
</style>
